import React, { Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { getBranchById, getOrganizerById, getTournamentsByBranchId, getTournamentsByOrganizerId, MockTournament } from '@/data/mockTableBookingData';
import { apiService } from '@/services/api';
import styles from './BranchTournaments.module.scss';

interface Tournament {
  id: string;
  name: string;
  description?: string;
  startDate: string;
  endDate: string;
  registrationDeadline: string;
  entryFee: number;
  maxParticipants: number;
  currentParticipants: number;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  format?: string;
  prizePool?: number;
  is_registration_open?: boolean;
  is_customer_registered?: boolean;
}

interface BranchTournamentsState {
  branch: any;
  organizer: any;
  tournaments: Tournament[];
  isLoading: boolean;
  error: string | null;
  registeringTournaments: Set<string>;
  registeredTournaments: Set<string>;
  unregisteringTournaments: Set<string>;
  showSuccessModal: boolean;
  successMessage?: string;
  showUnregisterModal: boolean;
  tournamentToUnregister: string | null;
  showErrorModal: boolean;
  errorMessage?: string;
}

function withNavigation(WrappedComponent: any) {
  return function WithNavigationComponent(props: any) {
    const navigate = useNavigate();
    const params = useParams();
    return <WrappedComponent {...props} navigate={navigate} params={params} />;
  };
}

class BranchTournaments extends Component<any, BranchTournamentsState> {
  constructor(props: any) {
    super(props);
    this.state = {
      branch: null,
      organizer: null,
      tournaments: [],
      isLoading: true,
      error: null,
      registeringTournaments: new Set(),
      registeredTournaments: new Set(),
      unregisteringTournaments: new Set(),
      showSuccessModal: false,
      showUnregisterModal: false,
      tournamentToUnregister: null,
      showErrorModal: false,
      errorMessage: undefined
    };
  }

  async componentDidMount() {
    const { organizerId, branchId } = this.props.params || {};
    if (organizerId && branchId) {
      await this.loadData(organizerId, branchId);
    }
  }

  loadData = async (organizerId: string, branchId: string) => {
    this.setState({ isLoading: true, error: null });
    
    // Load branch and organizer info immediately
    const branch = getBranchById(branchId);
    const organizer = getOrganizerById(organizerId);
    
    // Try to load from API with a short timeout (3 seconds)
    let apiTournaments: Tournament[] = [];
    let apiError: any = null;
    
    try {
      // Pass branchId to filter tournaments for this specific branch
      const tourRes = await Promise.race([
        apiService.getOrganizerTournaments(organizerId, branchId),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 3000)
        )
      ]) as any;
      
      // API response structure: { success: true, data: { organizerId, count, tournaments: [...] } }
      let tournamentsContainer: any = (tourRes && (tourRes as any).data) ?? tourRes;
      let tournamentsRaw: any = Array.isArray(tournamentsContainer)
        ? tournamentsContainer
        : tournamentsContainer?.tournaments || tournamentsContainer?.data || [];
      
      console.log('[BranchTournaments] Tournaments loaded for branch:', {
        branchId,
        organizerId,
        tournamentsCount: tournamentsRaw?.length || 0,
        responseStructure: tournamentsContainer
      });

      // Transform tournaments to our format
      apiTournaments = tournamentsRaw.map((tour: any) => ({
        id: tour.id || tour._id || `tour-${Date.now()}-${Math.random()}`,
        name: tour.name || tour.tournamentName || 'Unnamed Tournament',
        description: tour.description,
        startDate: tour.startDate || tour.start_date || tour.tournamentDate || new Date().toISOString(),
        endDate: tour.endDate || tour.end_date || new Date().toISOString(),
        registrationDeadline: tour.registrationDeadline || tour.registration_deadline || new Date().toISOString(),
        entryFee: tour.entryFee || tour.entry_fee || 0,
        maxParticipants: tour.maxParticipants || tour.max_participants || tour.maxPlayers || 16,
        currentParticipants: tour.currentParticipants || tour.current_participants || 0,
        status: this.getTournamentStatus(tour),
        format: tour.format || tour.gameType || '8-Ball',
        prizePool: tour.prizePool || tour.prize_pool,
        is_registration_open: tour.is_registration_open !== undefined ? tour.is_registration_open : true,
        is_customer_registered: tour.is_customer_registered !== undefined ? tour.is_customer_registered : false
      }));
      
      // Update registered tournaments set based on API response
      const registeredIds = apiTournaments
        .filter(t => t.is_customer_registered)
        .map(t => t.id);
      this.setState(prevState => ({
        registeredTournaments: new Set([...prevState.registeredTournaments, ...registeredIds])
      }));
    } catch (error: any) {
      console.warn('API call failed or timed out, using mock data:', error);
      apiError = error;
    }
    
    // Use API tournaments if available, otherwise fall back to mock data
    let finalTournaments: Tournament[] = [];
    
    if (apiTournaments.length > 0) {
      // Filter by branch if we have branch-specific tournaments
      finalTournaments = apiTournaments;
    } else {
      // Use mock data as fallback
      const mockTournaments = branchId 
        ? getTournamentsByBranchId(branchId)
        : getTournamentsByOrganizerId(organizerId);
      
      finalTournaments = mockTournaments.map((tour: MockTournament) => ({
        id: tour.id,
        name: tour.name,
        description: tour.description,
        startDate: tour.startDate,
        endDate: tour.endDate,
        registrationDeadline: tour.registrationDeadline,
        entryFee: tour.entryFee,
        maxParticipants: tour.maxParticipants,
        currentParticipants: tour.currentParticipants,
        status: tour.status,
        format: tour.format,
        prizePool: tour.prizePool
      }));
    }

    this.setState({
      branch: branch || null,
      organizer: organizer || null,
      tournaments: finalTournaments,
      isLoading: false,
      error: apiError && apiTournaments.length === 0 ? null : null // Don't show error if we have mock data
    });
  };

  getTournamentStatus = (tour: any): 'upcoming' | 'ongoing' | 'completed' | 'cancelled' => {
    if (tour.status) {
      if (['upcoming', 'ongoing', 'completed', 'cancelled'].includes(tour.status.toLowerCase())) {
        return tour.status.toLowerCase() as any;
      }
    }
    
    const now = new Date();
    const startDate = new Date(tour.startDate || tour.start_date);
    const endDate = new Date(tour.endDate || tour.end_date);
    
    if (now < startDate) return 'upcoming';
    if (now >= startDate && now <= endDate) return 'ongoing';
    if (now > endDate) return 'completed';
    return 'upcoming';
  };

  handleBackClick = () => {
    const { organizerId, branchId } = this.props.params || {};
    // Back to the branch hub (Tables / Rooms / Tournaments), where this page is reached from.
    this.props.navigate(`/customer/organizers/${organizerId}/branches/${branchId}`);
  };

  handleTournamentClick = (tournamentId: string) => {
    this.props.navigate(`/tournament/${tournamentId}`);
  };

  handleRegisterTournament = async (tournamentId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Find tournament to get entry fee
    const tournament = this.state.tournaments.find(t => t.id === tournamentId);
    if (!tournament) {
      alert('Tournament not found');
      return;
    }

    // Check if registration is open
    if (tournament.is_registration_open === false) {
      alert('Registration is closed for this tournament');
      return;
    }

    // Check if already registered
    if (tournament.is_customer_registered || this.state.registeredTournaments.has(tournamentId)) {
      alert('You are already registered for this tournament');
      return;
    }
    
    this.setState(prevState => ({
      registeringTournaments: new Set(prevState.registeringTournaments).add(tournamentId)
    }));

    try {
      // Call registration API with payment data
      const response = await apiService.registerForTournament(tournamentId, {
        paymentAmount: tournament.entryFee,
        paymentMethod: 'upi',
        paymentStatus: 'pending'
      });
      
      if (response.success && response.data) {
        const registrationData = response.data as any;
        const isAlreadyRegistered = registrationData.alreadyRegistered === true;
        
        this.setState(prevState => ({
          registeredTournaments: new Set(prevState.registeredTournaments).add(tournamentId),
          registeringTournaments: (() => {
            const newSet = new Set(prevState.registeringTournaments);
            newSet.delete(tournamentId);
            return newSet;
          })(),
          showSuccessModal: true,
          successMessage: isAlreadyRegistered
            ? `You are already registered for tournament "${tournament.name}"`
            : `Successfully registered for tournament "${tournament.name}"`
        }));

        // Reload tournaments to update participant count and registration status
        const { organizerId, branchId } = this.props.params || {};
        if (organizerId) {
          setTimeout(() => {
            this.loadData(organizerId, branchId || '');
          }, 1000);
        }
      } else {
        throw new Error(response.message || 'Failed to register for tournament');
      }
    } catch (error: any) {
      console.error('[BranchTournaments] Registration error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to register for tournament. Please try again.';
      alert(errorMessage);
      this.setState(prevState => {
        const newSet = new Set(prevState.registeringTournaments);
        newSet.delete(tournamentId);
        return { registeringTournaments: newSet };
      });
    }
  };

  handleUnregisterTournament = async (tournamentId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Find tournament
    const tournament = this.state.tournaments.find(t => t.id === tournamentId);
    if (!tournament) {
      this.setState({
        showErrorModal: true,
        errorMessage: 'Tournament not found'
      });
      return;
    }

    // Check if registration is open (required for unregistration)
    if (tournament.is_registration_open === false) {
      this.setState({
        showErrorModal: true,
        errorMessage: 'Cannot unregister. Tournament registration is closed'
      });
      return;
    }

    // Check if registered
    if (!tournament.is_customer_registered && !this.state.registeredTournaments.has(tournamentId)) {
      this.setState({
        showErrorModal: true,
        errorMessage: 'You are not registered for this tournament'
      });
      return;
    }

    // Show confirmation modal
    this.setState({
      showUnregisterModal: true,
      tournamentToUnregister: tournamentId
    });
  };

  handleConfirmUnregister = async () => {
    const { tournamentToUnregister } = this.state;
    if (!tournamentToUnregister) {
      return;
    }

    const tournamentId = tournamentToUnregister;
    
    // Close modal
    this.setState({
      showUnregisterModal: false,
      tournamentToUnregister: null
    });
    
    this.setState(prevState => ({
      unregisteringTournaments: new Set(prevState.unregisteringTournaments).add(tournamentId)
    }));

    try {
      // Call unregistration API
      const response = await apiService.unregisterFromTournament(tournamentId);
      
      if (response.success) {
        this.setState(prevState => ({
          registeredTournaments: (() => {
            const newSet = new Set(prevState.registeredTournaments);
            newSet.delete(tournamentId);
            return newSet;
          })(),
          unregisteringTournaments: (() => {
            const newSet = new Set(prevState.unregisteringTournaments);
            newSet.delete(tournamentId);
            return newSet;
          })(),
          showSuccessModal: true,
          successMessage: 'Successfully unregistered from tournament'
        }));

        // Reload tournaments to update participant count and registration status
        const { organizerId, branchId } = this.props.params || {};
        if (organizerId) {
          setTimeout(() => {
            this.loadData(organizerId, branchId || '');
          }, 1000);
        }
      } else {
        throw new Error(response.message || 'Failed to unregister from tournament');
      }
    } catch (error: any) {
      console.error('[BranchTournaments] Unregistration error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to unregister from tournament. Please try again.';
      this.setState(prevState => {
        const newSet = new Set(prevState.unregisteringTournaments);
        newSet.delete(tournamentId);
        return { 
          unregisteringTournaments: newSet,
          showErrorModal: true,
          errorMessage: errorMessage
        };
      });
    }
  };

  formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { label: string; color: string; bgColor: string } } = {
      upcoming: { label: 'Upcoming', color: '#3b82f6', bgColor: '#dbeafe' },
      ongoing: { label: 'Ongoing', color: '#10b981', bgColor: '#d1fae5' },
      completed: { label: 'Completed', color: '#6b7280', bgColor: '#f3f4f6' },
      cancelled: { label: 'Cancelled', color: '#ef4444', bgColor: '#fee2e2' }
    };

    const config = statusConfig[status] || statusConfig.upcoming;
    return (
      <span
        className={styles.statusBadge}
        style={{ color: config.color, backgroundColor: config.bgColor }}
      >
        {config.label}
      </span>
    );
  };

  render() {
    const { branch, organizer, tournaments, isLoading, error, registeringTournaments, registeredTournaments, unregisteringTournaments, showSuccessModal, successMessage, showUnregisterModal, tournamentToUnregister, showErrorModal, errorMessage } = this.state;

    if (isLoading) {
      return (
        <>
          <DashboardHeader />
          <div className={styles.branchTournamentsContainer}>
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
              <p>Loading tournaments...</p>
            </div>
          </div>
          <Footer />
        </>
      );
    }

    return (
      <>
        <DashboardHeader />
        <div className={styles.branchTournamentsContainer}>
          <div className={styles.branchTournamentsContent}>
            {/* Back Button */}
            <button onClick={this.handleBackClick} className={styles.backButton}>
              ← Back
            </button>

            {/* Header */}
            <div className={styles.headerSection}>
              <div>
                <h1 className={styles.pageTitle}>Tournaments</h1>
                {branch && (
                  <p className={styles.pageSubtitle}>
                    {branch.name} - {organizer?.name || 'Organizer'}
                  </p>
                )}
              </div>
            </div>

            {/* Unregister Confirmation Modal */}
            {showUnregisterModal && tournamentToUnregister && (() => {
              const tournament = tournaments.find(t => t.id === tournamentToUnregister);
              return (
                <div className={styles.modalOverlay} onClick={() => this.setState({ showUnregisterModal: false, tournamentToUnregister: null })}>
                  <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                      <h2>Confirm Unregistration</h2>
                      <button className={styles.modalClose} onClick={() => this.setState({ showUnregisterModal: false, tournamentToUnregister: null })}>×</button>
                    </div>
                    <div className={styles.modalBody}>
                      <div className={styles.confirmIcon}>⚠️</div>
                      <p className={styles.confirmMessage}>
                        Are you sure you want to unregister from <strong>{tournament?.name || 'this tournament'}</strong>?
                      </p>
                      <p className={styles.confirmSubtext}>
                        You can register again later if registration is still open.
                      </p>
                    </div>
                    <div className={styles.modalFooter}>
                      <button 
                        className={styles.cancelButton} 
                        onClick={() => this.setState({ showUnregisterModal: false, tournamentToUnregister: null })}
                      >
                        Cancel
                      </button>
                      <button 
                        className={styles.confirmButton} 
                        onClick={this.handleConfirmUnregister}
                      >
                        Yes, Unregister
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Error Modal */}
            {showErrorModal && (
              <div className={styles.modalOverlay} onClick={() => this.setState({ showErrorModal: false, errorMessage: undefined })}>
                <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                  <div className={styles.modalHeader}>
                    <h2>Error</h2>
                    <button className={styles.modalClose} onClick={() => this.setState({ showErrorModal: false, errorMessage: undefined })}>×</button>
                  </div>
                  <div className={styles.modalBody}>
                    <div className={styles.errorIcon}>❌</div>
                    <p className={styles.errorMessage}>
                      {errorMessage || 'An error occurred'}
                    </p>
                  </div>
                  <div className={styles.modalFooter}>
                    <button className={styles.closeButton} onClick={() => this.setState({ showErrorModal: false, errorMessage: undefined })}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && (
              <div className={styles.modalOverlay} onClick={() => this.setState({ showSuccessModal: false })}>
                <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                  <div className={styles.modalHeader}>
                    <h2>Registration Successful!</h2>
                    <button className={styles.modalClose} onClick={() => this.setState({ showSuccessModal: false })}>×</button>
                  </div>
                  <div className={styles.modalBody}>
                    <p>{successMessage}</p>
                  </div>
                  <div className={styles.modalFooter}>
                    <button className={styles.closeButton} onClick={() => this.setState({ showSuccessModal: false })}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className={styles.errorState}>
                <div className={styles.errorIcon}>⚠️</div>
                <h3>Error Loading Tournaments</h3>
                <p>{error}</p>
                <button 
                  onClick={() => {
                    const { organizerId, branchId } = this.props.params || {};
                    if (organizerId && branchId) {
                      this.loadData(organizerId, branchId);
                    }
                  }} 
                  className={styles.retryButton}
                >
                  Retry
                </button>
              </div>
            )}

            {/* Tournaments List */}
            {!error && tournaments.length === 0 && !isLoading ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>🏆</div>
                <h3>No tournaments available</h3>
                <p>There are no tournaments scheduled for this branch at the moment.</p>
                <button onClick={this.handleBackClick} className={styles.emptyStateButton}>
                  Back to branch
                </button>
              </div>
            ) : !error && tournaments.length > 0 ? (
              <div className={styles.tournamentsGrid}>
                {tournaments.map(tournament => {
                  const isRegistering = registeringTournaments.has(tournament.id);
                  const isUnregistering = unregisteringTournaments.has(tournament.id);
                  // Use API response fields if available, otherwise fall back to state
                  const isRegistered = tournament.is_customer_registered !== undefined 
                    ? tournament.is_customer_registered 
                    : registeredTournaments.has(tournament.id);
                  // Use API response field if available, otherwise check deadline
                  const isRegistrationOpen = tournament.is_registration_open !== undefined
                    ? tournament.is_registration_open
                    : new Date(tournament.registrationDeadline) > new Date();
                  const isFull = tournament.currentParticipants >= tournament.maxParticipants;

                  return (
                    <div
                      key={tournament.id}
                      className={styles.tournamentCard}
                      onClick={() => this.handleTournamentClick(tournament.id)}
                    >
                      <div className={styles.tournamentHeader}>
                        <h3 className={styles.tournamentName}>{tournament.name}</h3>
                        {this.getStatusBadge(tournament.status)}
                      </div>

                      {tournament.description && (
                        <p className={styles.tournamentDescription}>{tournament.description}</p>
                      )}

                      <div className={styles.tournamentDetails}>
                        <div className={styles.detailItem}>
                          <span className={styles.detailIcon}>📅</span>
                          <div className={styles.detailContent}>
                            <span className={styles.detailLabel}>Start:</span>
                            <span className={styles.detailValue}>{this.formatDate(tournament.startDate)}</span>
                          </div>
                        </div>
                        <div className={styles.detailItem}>
                          <span className={styles.detailIcon}>🏁</span>
                          <div className={styles.detailContent}>
                            <span className={styles.detailLabel}>End:</span>
                            <span className={styles.detailValue}>{this.formatDate(tournament.endDate)}</span>
                          </div>
                        </div>
                        <div className={styles.detailItem}>
                          <span className={styles.detailIcon}>💰</span>
                          <div className={styles.detailContent}>
                            <span className={styles.detailLabel}>Entry Fee:</span>
                            <span className={styles.detailValue}>${tournament.entryFee.toFixed(2)}</span>
                          </div>
                        </div>
                        {tournament.prizePool && (
                          <div className={styles.detailItem}>
                            <span className={styles.detailIcon}>🏆</span>
                            <div className={styles.detailContent}>
                              <span className={styles.detailLabel}>Prize Pool:</span>
                              <span className={styles.detailValue}>${tournament.prizePool.toFixed(2)}</span>
                            </div>
                          </div>
                        )}
                        <div className={styles.detailItem}>
                          <span className={styles.detailIcon}>👥</span>
                          <div className={styles.detailContent}>
                            <span className={styles.detailLabel}>Participants:</span>
                            <span className={styles.detailValue}>
                              {tournament.currentParticipants} / {tournament.maxParticipants}
                            </span>
                          </div>
                        </div>
                        {tournament.format && (
                          <div className={styles.detailItem}>
                            <span className={styles.detailIcon}>📋</span>
                            <div className={styles.detailContent}>
                              <span className={styles.detailLabel}>Format:</span>
                              <span className={styles.detailValue}>{tournament.format}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className={styles.tournamentActions}>
                        <button
                          className={styles.viewDetailsButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            this.handleTournamentClick(tournament.id);
                          }}
                        >
                          View Details
                        </button>
                        {!isRegistrationOpen && isRegistered ? (
                          <>
                            <button className={styles.registeredButtonDisabled} disabled>
                              ✓ Registered
                            </button>
                            <button className={styles.closedButton} disabled>
                              Registration Closed
                            </button>
                          </>
                        ) : !isRegistrationOpen ? (
                          <button className={styles.closedButton} disabled>
                            Registration Closed
                          </button>
                        ) : isRegistered ? (
                          <>
                            <button className={styles.registeredButton} disabled>
                              ✓ Registered
                            </button>
                            <button
                              className={styles.unregisterButton}
                              onClick={(e) => this.handleUnregisterTournament(tournament.id, e)}
                              disabled={isUnregistering}
                            >
                              {isUnregistering ? 'Unregistering...' : 'Unregister'}
                            </button>
                          </>
                        ) : isFull ? (
                          <button className={styles.fullButton} disabled>
                            Tournament Full
                          </button>
                        ) : (
                          <button
                            className={styles.registerButton}
                            onClick={(e) => this.handleRegisterTournament(tournament.id, e)}
                            disabled={isRegistering}
                          >
                            {isRegistering ? 'Registering...' : 'Register Now'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
        <Footer />
      </>
    );
  }
}

export default withNavigation(BranchTournaments);

